import { useState, useEffect, useRef } from 'react';
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg';
import { Colors } from '../../../../Styles/Colors';
import { CommonPatientDetails } from '../../../../Components/Common Components/CommonComponents';
import {
    Box, Button, Divider, IconButton, MenuItem, TextField, Tooltip, Typography
} from '@mui/material';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import { qDoctorPatientId, qDoctorAppointmentId } from '../../../../Utility/Services/DoctorPatientQueryIds';
import { getCachevalue, localGetItem } from '../../../../Utility/Services/CacheProviderService';
import Loader from '../../../../Components/Loader';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import { withTranslation } from 'react-i18next';
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import {
    handleCertificateRichTextPaste,
    CERTIFICATE_EDITOR_FIXED_FONT_FAMILY,
    CERTIFICATE_EDITOR_FIXED_FONT_SIZE,
} from '../../Common/certificateEditorPasteUtils'

const PREDEFINED_FIELDS = [ 'Patient Name', 'UHID', 'Gender', 'Age', 'Mobile No' ,'Doctor Name', 'Hospital Name', 'OP Number', "Purpose of Visit" ]


const DocCustomCertificate = (props) => {
    const editorRef = useRef(null)
    const savedRangeRef = useRef(null)

    const [patientDetails, setPatientDetails] = useState({})
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [templateList, setTemplateList] = useState([])
    const [originalContent, setOriginalContent] = useState('')
    const [keyValues, setKeyValues] = useState({})
    const [bold, setBold] = useState(false)
    const [italic, setItalic] = useState(false)
    const [underline, setUnderline] = useState(false)
    const [isLoader, setIsLoader] = useState(false)
    const [isErrorMsg, setIsErrorMsg] = useState(false)
    const [isErrorMsgText, setIsErrorMsgText] = useState('')
    const [successMsg, setSuccessMsg] = useState(false)
    const [successMsgText, setSuccessMsgText] = useState('')

    useEffect(() => {
        const details = JSON.parse(getCachevalue('DoctorPatientData') || '{}')
        setPatientDetails(details)
        getCertificateTxt(details)
        getTempData()
    }, [])

    const errorMessage = (msg) => { setIsErrorMsgText(msg); setIsErrorMsg(true) }
    const successMessage = (msg) => { setSuccessMsgText(msg); setSuccessMsg(true) }

    const msgClose = () => {
        setIsErrorMsg(false); setIsErrorMsgText('')
        setSuccessMsg(false); setSuccessMsgText('')
    }

    const LoaderFunction = (value) => {
        setIsLoader(value)
    }

    /** Template builder marks field tags with contentEditable=false; that blocks list/align execCommand here. */
    const relaxCertificateFieldSpans = (editor) => {
        if (!editor) return
        editor.querySelectorAll('[contenteditable="false"]').forEach((el) => {
            el.contentEditable = 'true'
        })
    }

    const setEditorHtmlWithLoosenedFields = (html) => {
        const ed = editorRef.current
        if (!ed) return
        ed.innerHTML = html
        relaxCertificateFieldSpans(ed)
    }

    /**
     * Normalizes editor HTML before save so backend print rendering receives
     * deterministic style strings and explicit block alignment.
     */
    const normalizeCertificateHtmlForSave = (html) => {
        if (!html) return ''

        const holder = document.createElement('div')
        holder.innerHTML = html

        const cleanStyleAttribute = (el) => {
            const styleValue = el.getAttribute('style')
            if (!styleValue) return

            const styleMap = new Map()
            styleValue
                .split(';')
                .map((rule) => rule.trim())
                .filter(Boolean)
                .forEach((rule) => {
                    const idx = rule.indexOf(':')
                    if (idx === -1) return
                    const prop = rule.slice(0, idx).trim().toLowerCase()
                    const value = rule.slice(idx + 1).trim().replace(/\s+/g, ' ')
                    if (!prop || !value) return
                    styleMap.set(prop, value)
                })

            // Strip editor-only styles that should not affect print layout.
            styleMap.delete('cursor')
            styleMap.delete('user-select')

            const normalized = [...styleMap.entries()].map(([prop, value]) => `${prop}: ${value}`).join('; ')
            if (normalized) {
                el.setAttribute('style', `${normalized};`)
            } else {
                el.removeAttribute('style')
            }
        }

        const BLOCK_ALIGN_SELECTOR = 'div, p, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote, pre, td, th'

        holder.querySelectorAll('*').forEach((el) => {
            cleanStyleAttribute(el)
            el.removeAttribute('contenteditable')
            el.removeAttribute('spellcheck')
        })

        holder.querySelectorAll(BLOCK_ALIGN_SELECTOR).forEach((blockEl) => {
            const styleAlign = blockEl.style?.textAlign?.trim()?.toLowerCase()
            const attrAlign = (blockEl.getAttribute('align') || '').trim().toLowerCase()
            const finalAlign = styleAlign || attrAlign

            if (finalAlign && ['left', 'center', 'right', 'justify'].includes(finalAlign)) {
                blockEl.style.textAlign = finalAlign
                blockEl.setAttribute('align', finalAlign)
            } else {
                blockEl.removeAttribute('align')
            }
        })

        return holder.innerHTML
    }

    const getCertificateTxt = (details = JSON.parse(getCachevalue('DoctorPatientData') || '{}')) => {
        try {
            const clinic_id = localGetItem("SelectedDoctorClinicId")
            const patientDetails = JSON.parse(getCachevalue('DoctorPatientData'));
            RestAPIService.getAll(`${Serviceurls.CUSTOM_CERTIFICATE_GET}?clinic_id=${clinic_id}&appointment_id=${qDoctorAppointmentId(patientDetails?.appointment_id)}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const resData = response?.data?.data || response?.data || {}
                        const desc = resData?.description || ""
                        if (editorRef.current) setEditorHtmlWithLoosenedFields(desc)
                        // If response has used_keys, set as selected template to show key fields
                        if (resData?.used_keys?.length) {
                            // Reverse replaced values back to <Key> placeholders to get original template
                            let templateHtml = desc
                            const savedValues = {}
                            resData.used_keys.forEach(k => {
                                if (k.value) {
                                    savedValues[k.key_name] = k.value
                                    const escapedVal = k.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                    templateHtml = templateHtml.replace(new RegExp(escapedVal, 'gi'), `&lt;${k.key_name}&gt;`)
                                }
                            })
                            setSelectedTemplate({
                                template_id: resData?.template_id || '',
                                template_name: resData?.template_name || '',
                                description: templateHtml,
                                used_keys: resData.used_keys
                            })
                            setOriginalContent(templateHtml)
                            setKeyValues(savedValues)
                        }
                    }
                })
                .catch((e) => errorMessage(e.response?.data?.message || e.message))
        } catch (e) {
            errorMessage(e.message)
        }
    }

    const getTempData = () => {
        try {
            const clinic_id = localGetItem("SelectedDoctorClinicId")
            RestAPIService.getAll(`${Serviceurls.CUSTOM_CERTIFICATE_TEMPLATE}?clinic_id=${clinic_id}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setTemplateList(response?.data?.data?.filter((t) => t.is_active) || [])
                    }
                })
                .catch((e) => errorMessage(e.response?.data?.message || e.message))
        } catch (e) {
            errorMessage(e.message)
        }
    }

    const handlePrint = () => {
        try {
            const clinic_id = localGetItem("SelectedDoctorClinicId")
            RestAPIService.getAll(`${Serviceurls.CUSTOM_CERTIFICATE_GET}?clinic_id=${clinic_id}&appointment_id=${qDoctorAppointmentId(patientDetails?.appointment_id)}&export_type=pdf`)
                .then((response) => {
                    const file = new Blob([response.data], { type: 'application/pdf' })
                    window.open(URL.createObjectURL(file))
                })
                .catch((e) => errorMessage(e?.response?.data?.message || e.message))
        } catch (e) {
            errorMessage(e.message)
        }
    }

    // ── Template selection: load HTML + reset key fields ───────────────────
    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template)
        let PatientDetails = JSON.parse(getCachevalue('DoctorPatientData') || '{}')
        const initialKeyValues = {}
        const predefinedMap = {
            'Patient Name': PatientDetails?.patient_name || PatientDetails?.patient_names || '',
            'UHID': PatientDetails?.patient_account_number || '',
            'Gender': PatientDetails?.patient_gender || '',
            'Age': PatientDetails?.patient_age || '',
            'Mobile No': PatientDetails?.patient_mobile_number || '',
            'Doctor Name': PatientDetails?.doctor_name || '',
            'Hospital Name': localGetItem("BranchName") || '',
            'OP Number': PatientDetails?.op_number || '',
            'Purpose of Visit': PatientDetails?.purpose_of_visit || ''
        };
        (template.used_keys || []).forEach(k => {
            if (predefinedMap[k.key_name] !== undefined) {
                initialKeyValues[k.key_name] = predefinedMap[k.key_name]
            }
        })
        setKeyValues(structuredClone(initialKeyValues))
        // Store raw template with placeholders as originalContent
        setOriginalContent(template.description)
        // Build display HTML by replacing placeholders with prefilled values
        let html = template.description
        Object.entries(initialKeyValues).forEach(([k, v]) => {
            const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            if (v) {
                const keyInfo = (template.used_keys || []).find(ku => ku.key_name === k)
                const displayVal = keyInfo?.type === 'date' ? formatDateValue(v) : v
                html = html.replace(new RegExp(`&lt;${escaped}&gt;`, 'gi'), displayVal)
            }
        })
        setEditorHtmlWithLoosenedFields(html)
    }

    // Format ISO date (yyyy-mm-dd) → dd-mm-yyyy for display in editor
    const formatDateValue = (isoVal) => {
        if (!isoVal || !isoVal.includes('-')) return isoVal
        const [y, m, d] = isoVal.split('-')
        return `${d}-${m}-${y}`
    }

    // ── Key field change: replace <Key> placeholders in editor ─────────────
    // Original description stores the raw template HTML with &lt;Key&gt; tags.
    // Each time any field changes, we recompute from originalContent so that
    // clearing a field restores the placeholder.
    const handleKeyChange = (keyName, val) => {
        const updated = { ...keyValues, [keyName]: val }
        setKeyValues(updated)

        // Always recompute display HTML from originalContent (template with &lt;Key&gt; placeholders).
        // Never reverse-replace live editor HTML — a short value like "sa" matches substrings
        // inside HTML attributes (e.g. sans-serif, rgba) and tag names, visibly corrupting markup.
        let html = originalContent
        Object.entries(updated).forEach(([k, v]) => {
            if (!v) return
            const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const keyInfo = selectedTemplate?.used_keys?.find(ku => ku.key_name === k)
            const displayVal = keyInfo?.type === 'date' ? formatDateValue(v) : v
            html = html.replace(new RegExp(`&lt;${escaped}&gt;`, 'gi'), displayVal)
        })
        setEditorHtmlWithLoosenedFields(html)
    }

    // ── Toolbar helpers ────────────────────────────────────────────────────
    const saveCursorRange = () => {
        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0)
            if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
                savedRangeRef.current = range.cloneRange()
            }
        }
    }

    const restoreRange = () => {
        const editor = editorRef.current
        if (!editor) return false
        editor.focus()
        if (savedRangeRef.current) {
            const range = savedRangeRef.current
            if (editor.contains(range.startContainer) && editor.contains(range.endContainer)) {
                try {
                    const sel = window.getSelection()
                    sel.removeAllRanges()
                    sel.addRange(range)
                    return true
                } catch (e) {
                    savedRangeRef.current = null
                }
            } else {
                savedRangeRef.current = null
            }
        }
        return false
    }

    /** Stops the toolbar control from taking focus so the contentEditable selection stays active. */
    const keepSelectionOnToolbarPointerDown = (e) => {
        e.preventDefault()
    }

    const execCmd = (cmd, value = null) => {
        editorRef.current?.focus()
        document.execCommand(cmd, false, value)
        updateToolbarState()
        saveCursorRange()
    }
   
    /** True when an &lt;li&gt; has no visible text and no meaningful embedded nodes (e.g. field tags). */
    const isListItemEffectivelyEmpty = (li) => {
        const text = (li.innerText || '').replace(/\u00A0/g, ' ').trim()
        if (text.length > 0) return false
        if (li.querySelector('img, span[contenteditable="false"]')) return false
        return true
    }

    /**
     * Browsers turn each block (including blank lines) into its own list item.
     * Remove empty &lt;li&gt;s so spacing lines do not get bullets/numbers.
     */
    const cleanupEmptyListItems = (editor) => {
        if (!editor) return
        let changed = true
        while (changed) {
            changed = false
            const lists = [...editor.querySelectorAll('ul, ol')]
            for (const list of lists) {
                for (const child of [...list.children]) {
                    if (child.tagName === 'LI' && isListItemEffectivelyEmpty(child)) {
                        child.remove()
                        changed = true
                    }
                }
                if (list.parentNode && list.children.length === 0) {
                    list.remove()
                    changed = true
                }
            }
        }
    }

    const applyListCommand = (command) => {
        const editor = editorRef.current
        if (!editor) return
        restoreRange()
        editor.focus()
        document.execCommand(command, false, null)
        cleanupEmptyListItems(editor)
        updateToolbarState()
        saveCursorRange()
    }

    const applyAlignment = (align) => {
        const BLOCK_TAGS = new Set([
            'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
            'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TD', 'TH'
        ])

        const editor = editorRef.current
        if (!editor) return
        restoreRange()
        editor.focus()
        const sel = window.getSelection()

        // No selection — fall back to execCommand on cursor position
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
            document.execCommand(
                align === 'left' ? 'justifyLeft'
                    : align === 'center' ? 'justifyCenter'
                        : align === 'right' ? 'justifyRight'
                            : 'justifyFull'
                , false, null)
            saveCursorRange()
            return
        }

        const range = sel.getRangeAt(0)

        // Walk all block elements inside the editor that overlap the selection
        const blocks = []
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT, {
            acceptNode: (node) => {
                if (!BLOCK_TAGS.has(node.tagName)) return NodeFilter.FILTER_SKIP
                const nodeRange = document.createRange()
                nodeRange.selectNode(node)
                const startToEnd = range.compareBoundaryPoints(Range.START_TO_END, nodeRange)
                const endToStart = range.compareBoundaryPoints(Range.END_TO_START, nodeRange)
                if (startToEnd > 0 && endToStart < 0) return NodeFilter.FILTER_ACCEPT
                return NodeFilter.FILTER_SKIP
            }
        })

        let node
        while ((node = walker.nextNode())) blocks.push(node)

        // Fallback: closest block ancestor of selection start
        if (blocks.length === 0) {
            let container = range.commonAncestorContainer
            if (container.nodeType === Node.TEXT_NODE) container = container.parentElement
            while (container && container !== editor) {
                if (BLOCK_TAGS.has(container.tagName)) {
                    blocks.push(container)
                    break
                }
                container = container.parentElement
            }
            if (blocks.length === 0) blocks.push(editor)
        }

        blocks.forEach(block => {
            block.style.textAlign = align

            // Fix bullet/number marker position:
            // list-style-position:outside (default) pins the marker to the left
            // regardless of text-align. Switch to 'inside' so the marker travels
            // with the text, and restore 'outside' only when aligning left.
            if (block.tagName === 'UL' || block.tagName === 'OL') {
                block.style.listStylePosition = align === 'left' ? 'outside' : 'inside'
                // Also reset padding so the list doesn't hang off-screen when right-aligned
                if (align === 'right') {
                    block.style.paddingLeft = '0'
                    block.style.paddingRight = '1.5em'
                } else if (align === 'center') {
                    block.style.paddingLeft = '0'
                    block.style.paddingRight = '0'
                } else {
                    block.style.paddingLeft = ''
                    block.style.paddingRight = ''
                }
            }

            if (block.tagName === 'LI') {
                block.style.listStylePosition = align === 'left' ? 'outside' : 'inside'
            }
        })
        saveCursorRange()
    }
    const updateToolbarState = () => {
        setBold(document.queryCommandState('bold'))
        setItalic(document.queryCommandState('italic'))
        setUnderline(document.queryCommandState('underline'))
    }

    // ── Save: console.log final data ───────────────────────────────────────
    const handleSave = () => {
        const finalHTML = normalizeCertificateHtmlForSave(editorRef.current?.innerHTML || '')
        const listMatch = templateList.find((t) => t.template_name === selectedTemplate?.template_name)
        const templateId = selectedTemplate?.template_id ?? selectedTemplate?.id ?? listMatch?.id ?? ''
        try {
            const clinic_id = localGetItem("SelectedDoctorClinicId")
            const usedKeys = (selectedTemplate?.used_keys || []).map(k => ({
                type: k.type,
                key_name: k.key_name,
                value: keyValues[k.key_name] || ''
            }))

            const data = {
                "clinic_id": clinic_id,
                "appointment_id": patientDetails?.appointment_id,
                "description": finalHTML,
                "used_keys": usedKeys,
                "template_id": templateId,
                "template_name": selectedTemplate?.template_name || '',
            }
            LoaderFunction(true);
            RestAPIService.create(data, Serviceurls.CUSTOM_CERTIFICATE_GET)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        getCertificateTxt()
                        successMessage(response?.data?.message)
                        LoaderFunction(false);
                    }
                }).catch((error) => {
                    LoaderFunction(false);
                    errorMessage(error?.response?.data?.message || error?.message)
                })
        } catch (e) {
            errorMessage(e?.message)
        }
    }

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <Box component={'div'} sx={{ backgroundColor: Colors.Background, height: '73vh', padding: '0.5vw', display: 'flex', flexDirection: 'column', gap: '0.5vw' }}>

            {/* Patient Header */}
            <Box component={'div'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Box component={'div'} className='doc_header_card' mr={'0.5vw'}>
                    <CommonPatientDetails data={patientDetails} showDetailed={true} fromDoctor={true} />
                </Box>
            </Box>

            {/* Main Two-Column Layout */}
            <Box sx={{ display: 'flex', flex: 1, gap: '0.5vw', overflow: 'hidden' }}>

                {/* ── Left: Rich Text Editor ── */}
                <Box sx={{
                    flex: 1, backgroundColor: 'white', borderRadius: '4px',
                    border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                }}>
                    {/* Toolbar Row: format buttons left, template dropdown right */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        borderBottom: '1px solid #e0e0e0', padding: '0.3vw 0.6vw',
                        backgroundColor: '#fafafa', flexShrink: 0
                    }}>
                        {/* Format Buttons — same as DocCusCertTempCreation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.1vw' }}>
                            {/* Undo / Redo */}
                            <Tooltip title="Undo" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('undo')} sx={{ padding: '0.2vw' }}>
                                    <UndoIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Redo" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('redo')} sx={{ padding: '0.2vw' }}>
                                    <RedoIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>

                            <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                            {/* Text Styles */}
                            <Tooltip title="Bold" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('bold')} sx={{ padding: '0.2vw', backgroundColor: bold ? '#e3f2fd' : 'transparent' }}>
                                    <FormatBoldIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Italic" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('italic')} sx={{ padding: '0.2vw', backgroundColor: italic ? '#e3f2fd' : 'transparent' }}>
                                    <FormatItalicIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Underline" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('underline')} sx={{ padding: '0.2vw', backgroundColor: underline ? '#e3f2fd' : 'transparent' }}>
                                    <FormatUnderlinedIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Strikethrough" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('strikeThrough')} sx={{ padding: '0.2vw' }}>
                                    <StrikethroughSIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>

                            <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                            {/* Lists */}
                            <Tooltip title="Bullet List" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyListCommand('insertUnorderedList')} sx={{ padding: '0.2vw' }}>
                                    <FormatListBulletedIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Numbered List" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyListCommand('insertOrderedList')} sx={{ padding: '0.2vw' }}>
                                    <FormatListNumberedIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Indent" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('indent')} sx={{ padding: '0.2vw' }}>
                                    <FormatIndentIncreaseIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>

                            <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                            {/* Alignment */}
                            <Tooltip title="Align Left" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('left')} sx={{ padding: '0.2vw' }}>
                                    <FormatAlignLeftIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Align Center" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('center')} sx={{ padding: '0.2vw' }}>
                                    <FormatAlignCenterIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Align Right" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('right')} sx={{ padding: '0.2vw' }}>
                                    <FormatAlignRightIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Justify" placement='top'>
                                <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('justify')} sx={{ padding: '0.2vw' }}>
                                    <FormatAlignJustifyIcon sx={{ fontSize: '1.1vw' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Select Template Dropdown */}
                        <TextField
                            select
                            size='small'
                            value={selectedTemplate?.template_name || ''}
                            onChange={(e) => {
                                const tmpl = templateList.find(t => t.template_name === e.target.value)
                                if (tmpl) handleTemplateSelect(tmpl)
                            }}
                            sx={{
                                minWidth: '12vw',
                                '& .MuiInputBase-input': { fontSize: '0.8vw', padding: '0.3vw 1.8vw 0.3vw 0.6vw' },
                            }}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value='' disabled sx={{ fontSize: '0.8vw' }}>
                                Select Template
                            </MenuItem>
                            {templateList.map((tmpl) => (
                                <MenuItem key={tmpl.template_name} value={tmpl.template_name} sx={{ fontSize: '0.8vw' }}>
                                    {tmpl.template_name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* Editable Content Area */}
                    <Box
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onMouseUp={() => { saveCursorRange(); updateToolbarState() }}
                        onKeyUp={() => { saveCursorRange(); updateToolbarState() }}
                        onSelect={() => {
                            saveCursorRange()
                            updateToolbarState()
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;')
                            }
                        }}
                        onPaste={(e) => {
                            handleCertificateRichTextPaste(e, editorRef.current)
                            requestAnimationFrame(() => {
                                saveCursorRange()
                                updateToolbarState()
                            })
                        }}
                        sx={{
                            flex: 1,
                            padding: '1vw 1.5vw',
                            outline: 'none',
                            overflowY: 'auto',
                            lineHeight: 1.7,
                            fontFamily: CERTIFICATE_EDITOR_FIXED_FONT_FAMILY,
                            fontSize: CERTIFICATE_EDITOR_FIXED_FONT_SIZE,
                            /* Force one face/size for all body + pasted nodes (inline styles cannot enlarge text). */
                            '&, & *': {
                                fontFamily: `${CERTIFICATE_EDITOR_FIXED_FONT_FAMILY} !important`,
                                fontSize: `${CERTIFICATE_EDITOR_FIXED_FONT_SIZE} !important`,
                            },
                            '&:empty:before': {
                                content: '"Select a template or start typing..."',
                                color: '#bbb',
                                pointerEvents: 'none'
                            },
                        }}
                    />
                </Box>

                {/* ── Right: Keys Panel ── */}
                <Box sx={{
                    width: '22vw', backgroundColor: 'white', borderRadius: '4px',
                    border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                }}>
                    {/* Template Name as Header */}
                    <Box sx={{ padding: '0.6vw 1vw', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.85vw', fontWeight: 600, color: '#444' }}>
                            {selectedTemplate?.template_name || 'No Template Selected'}
                        </Typography>
                    </Box>

                    {/* Key → TextField rows */}
                    <Box sx={{ flex: 1, overflowY: 'auto', padding: '0.8vw 1vw', display: 'flex', flexDirection: 'column', gap: '0.8vw' }}>
                        {selectedTemplate ? (
                            selectedTemplate.used_keys.map((keyObj) => (
                                <Box key={keyObj.key_name} sx={{ display: 'flex', flexDirection: 'column', gap: '0.25vw' }}>
                                    {/* Key label + type badge */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4vw' }}>
                                        <Typography sx={{ fontSize: '0.8vw', color: '#555' }}>
                                            {keyObj.key_name}
                                        </Typography>
                                        <Box sx={{
                                            backgroundColor: keyObj.type === 'date' ? '#fff3e0' : '#e3f2fd',
                                            color: keyObj.type === 'date' ? '#e65100' : '#1565c0',
                                            fontSize: '0.55vw', fontWeight: 700, padding: '0.05vw 0.3vw',
                                            borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.03em'
                                        }}>{keyObj.type}</Box>
                                    </Box>
                                    {/* Date picker or text input */}
                                    {keyObj.type === 'date' ? (
                                        <TextField
                                            size='small'
                                            type='date'
                                            fullWidth
                                            disabled={PREDEFINED_FIELDS.includes(keyObj.key_name)}
                                            value={keyValues[keyObj.key_name] || ''}
                                            onChange={(e) => handleKeyChange(keyObj.key_name, e.target.value)}
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.8vw', padding: '0.35vw 0.6vw' } }}
                                        />
                                    ) : (
                                        <TextField
                                            size='small'
                                            fullWidth
                                            placeholder={`Enter ${keyObj.key_name}`}
                                            value={keyValues[keyObj.key_name] || ''}
                                            disabled={PREDEFINED_FIELDS.includes(keyObj.key_name)}
                                            onChange={(e) => handleKeyChange(keyObj.key_name, e.target.value)}
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.8vw', padding: '0.35vw 0.6vw' } }}
                                        />
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Typography sx={{ fontSize: '0.8vw', color: '#bbb', textAlign: 'center', mt: '2vw' }}>
                                Select a template to see fields
                            </Typography>
                        )}
                    </Box>

                    {/* Save + Print */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'flex-end', gap: '0.6vw',
                        padding: '0.6vw 1vw', borderTop: '1px solid #e0e0e0', flexShrink: 0
                    }}>
                        <Button
                            variant='outlined'
                            size='small'
                            onClick={handleSave}
                            sx={{ textTransform: 'uppercase', fontSize: '0.78vw', minWidth: '4.5vw', borderColor: '#bbb', color: '#555' }}
                        >
                            Save
                        </Button>
                        <Button
                            variant='contained'
                            size='small'
                            onClick={handlePrint}
                            sx={{
                                textTransform: 'uppercase', fontSize: '0.78vw', minWidth: '4.5vw',
                                backgroundColor: '#04B7B1', '&:hover': { backgroundColor: '#039e99' }
                            }}
                        >
                            Print
                        </Button>
                    </Box>
                </Box>
            </Box>

            {isErrorMsg && <ToastMsg severity={'error'} msg={isErrorMsgText} msgPop={msgClose} />}
            {successMsg && <ToastMsg severity={'success'} msg={successMsgText} msgPop={msgClose} />}
            <Loader loaderOpen={isLoader} />
        </Box>
    )
}

export default withTranslation()(DocCustomCertificate)
